import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { ProfileNameLink } from "PFCore/helpers/profile_name_link";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { CustomValue, CustomValueState } from "PFTypes";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { humanize } from "underscore.string";

import { SkillState } from "../../table/cell/skill_state";
import { useSkillManagementSidePanel } from "../../table/hooks/use_skill_management_side_panel";
import css from "./skills_management_side_panel.module.scss";

export const INFO_BOX_LIST = ["state", "globalId", "source", "creator", "createdAt", "updatedAt"] as const;

type InfoBoxContent = JSX.Element | string;

interface InfoBoxProps {
  skill: CustomValue;
}

const InfoBox = ({ title, content }: { title: string; content: InfoBoxContent }) => (
  <div className={css.skillInfoBox}>
    <Typography variant="labelRegular" tag="p" className={css.boxLabel}>
      {title}
    </Typography>
    <div>{content}</div>
  </div>
);

export const InfoBoxes = ({ skill }: InfoBoxProps) => {
  const { formatDate } = useDateFormatter();
  const { t } = useTranslation("skillsManagement", { keyPrefix: "sidePanel.infoBoxes" });
  const { closeSidePanel } = useSkillManagementSidePanel();

  const getContent = useCallback(
    (infoBoxName: keyof CustomValue) => {
      const value = skill[infoBoxName];
      if (value) {
        switch (infoBoxName) {
          case "state":
            return <SkillState state={value as CustomValueState} />;
          case "globalId":
            return value ? <Icon name="pf-logo" /> : "-";
          case "source":
            return humanize(value as string);
          case "creator":
            return (
              <ProfileNameLink
                profile={{
                  id: (value as CustomValue["creator"])!.id,
                  firstName: skill.creator?.name?.split(" ")[0] || "",
                  lastName: skill.creator?.name?.split(" ")[1] || "",
                  viewable: true
                }}
                options={{ beforeShowPreview: closeSidePanel }}
              />
            );
          case "createdAt":
          case "updatedAt":
            return formatDate(value as string);
        }
      }
      return "-";
    },
    [formatDate, skill, closeSidePanel]
  );

  return (
    <div className={css.skillInfoBoxes}>
      {INFO_BOX_LIST.map((name: (typeof INFO_BOX_LIST)[number], index) => (
        <InfoBox key={index} content={getContent(name)} title={t(name)} />
      ))}
    </div>
  );
};
